/**
 * Timezone utilities using native Intl APIs.
 *
 * Uses IANA timezone database via Intl.supportedValuesOf('timeZone')
 * which is available in Node.js 18+ and modern browsers.
 */

// ============================================================
// Types
// ============================================================

/**
 * A timezone option for display in pickers.
 */
export interface TimezoneOption {
  /** IANA timezone identifier (e.g., 'America/New_York') */
  value: string;
  /** Human-readable label (e.g., 'Eastern Time - New York') */
  label: string;
  /** Region for grouping (e.g., 'Americas', 'Europe') */
  region: string;
  /** Current UTC offset in hours (e.g., -5 for EST) */
  offsetHours: number;
  /** Current UTC offset string (e.g., 'UTC-05:00') */
  offsetString: string;
}

/**
 * Regions for grouping timezones in pickers.
 */
export type TimezoneRegion = 'Americas' | 'Europe' | 'Asia' | 'Pacific' | 'Africa' | 'Other';

// ============================================================
// Core Functions
// ============================================================

/**
 * Get all supported IANA timezone identifiers.
 * Falls back to a comprehensive list if Intl.supportedValuesOf is not available.
 */
export function getAllTimezones(): string[] {
  try {
    // Modern browsers and Node.js 18+
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
      return (Intl as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf(
        'timeZone'
      );
    }
  } catch {
    // Fall through to curated list
  }

  // Fallback: comprehensive curated list
  return CURATED_TIMEZONES.map((tz) => tz.value);
}

/**
 * Get the current hour (0-23) in a specific timezone.
 *
 * @param timezone - IANA timezone identifier
 * @returns Current hour in that timezone
 */
export function getCurrentHourInTimezone(timezone: string): number {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date());
    const hourPart = parts.find((p) => p.type === 'hour');
    return hourPart ? parseInt(hourPart.value, 10) : new Date().getUTCHours();
  } catch {
    // Invalid timezone, fall back to UTC
    return new Date().getUTCHours();
  }
}

/**
 * Get the current time in a specific timezone.
 *
 * @param timezone - IANA timezone identifier
 * @returns Object with hour, minute, and formatted string
 */
export function getCurrentTimeInTimezone(timezone: string): {
  hour: number;
  minute: number;
  formatted: string;
} {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);

    return {
      hour,
      minute,
      formatted: formatter.format(now),
    };
  } catch {
    const now = new Date();
    return {
      hour: now.getUTCHours(),
      minute: now.getUTCMinutes(),
      formatted: `${now.getUTCHours()}:${now.getUTCMinutes().toString().padStart(2, '0')}`,
    };
  }
}

/**
 * Check if a timezone is currently at a specific hour.
 * Useful for cron jobs that need to send notifications at a specific local time.
 *
 * @param timezone - IANA timezone identifier
 * @param targetHour - The target hour (0-23)
 * @returns True if the timezone is currently at the target hour
 */
export function isTimezoneAtHour(timezone: string, targetHour: number): boolean {
  return getCurrentHourInTimezone(timezone) === targetHour;
}

/**
 * Get the UTC offset for a timezone at the current moment.
 *
 * @param timezone - IANA timezone identifier
 * @returns Offset in hours (e.g., -5 for EST, 1 for CET)
 */
export function getTimezoneOffset(timezone: string): number {
  try {
    const now = new Date();

    // Get UTC time components
    const utcFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const utcParts = Object.fromEntries(
      utcFormatter.formatToParts(now).map((p) => [p.type, p.value])
    );

    // Get timezone time components
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const tzParts = Object.fromEntries(
      tzFormatter.formatToParts(now).map((p) => [p.type, p.value])
    );

    // Calculate offset in minutes
    const utcMinutes =
      parseInt(utcParts.day) * 24 * 60 + parseInt(utcParts.hour) * 60 + parseInt(utcParts.minute);
    const tzMinutes =
      parseInt(tzParts.day) * 24 * 60 + parseInt(tzParts.hour) * 60 + parseInt(tzParts.minute);

    // Handle day boundary wrapping
    let diffMinutes = tzMinutes - utcMinutes;
    if (diffMinutes > 12 * 60) diffMinutes -= 24 * 60;
    if (diffMinutes < -12 * 60) diffMinutes += 24 * 60;

    return diffMinutes / 60;
  } catch {
    return 0;
  }
}

/**
 * Format a timezone offset as a string (e.g., 'UTC-05:00', 'UTC+01:00').
 */
export function formatTimezoneOffset(offsetHours: number): string {
  const sign = offsetHours >= 0 ? '+' : '-';
  const absHours = Math.abs(Math.floor(offsetHours));
  const minutes = Math.abs(Math.round((offsetHours % 1) * 60));
  return `UTC${sign}${absHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Validate that a string is a valid IANA timezone identifier.
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

// ============================================================
// Timezone Display Functions
// ============================================================

/**
 * Get a human-readable display name for a timezone.
 * Uses Intl.DateTimeFormat with timeZoneName option.
 *
 * @param timezone - IANA timezone identifier
 * @param style - 'short' (PST), 'long' (Pacific Standard Time), or 'city' (Los Angeles)
 */
export function getTimezoneDisplayName(
  timezone: string,
  style: 'short' | 'long' | 'city' = 'long'
): string {
  if (style === 'city') {
    // Extract city name from IANA identifier
    const parts = timezone.split('/');
    const city = parts[parts.length - 1].replace(/_/g, ' ');
    return city;
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: style === 'short' ? 'short' : 'long',
    });
    const parts = formatter.formatToParts(new Date());
    const tzNamePart = parts.find((p) => p.type === 'timeZoneName');
    return tzNamePart?.value ?? timezone;
  } catch {
    return timezone;
  }
}

/**
 * Get the region for a timezone based on its IANA identifier.
 */
export function getTimezoneRegion(timezone: string): TimezoneRegion {
  const prefix = timezone.split('/')[0];

  switch (prefix) {
    case 'America':
      return 'Americas';
    case 'Europe':
      return 'Europe';
    case 'Asia':
      return 'Asia';
    case 'Pacific':
    case 'Australia':
      return 'Pacific';
    case 'Africa':
      return 'Africa';
    default:
      return 'Other';
  }
}

/**
 * Build a TimezoneOption object for a given IANA timezone.
 */
export function buildTimezoneOption(timezone: string): TimezoneOption {
  const city = getTimezoneDisplayName(timezone, 'city');
  const shortName = getTimezoneDisplayName(timezone, 'short');
  const region = getTimezoneRegion(timezone);
  const offsetHours = getTimezoneOffset(timezone);
  const offsetString = formatTimezoneOffset(offsetHours);

  return {
    value: timezone,
    label: `${city} (${shortName})`,
    region,
    offsetHours,
    offsetString,
  };
}

// ============================================================
// Curated Timezone Lists
// ============================================================

/**
 * Curated list of common timezones for user selection.
 * These are the most commonly used timezones, organized by region.
 */
export const CURATED_TIMEZONES: TimezoneOption[] = [
  // Americas
  {
    value: 'America/New_York',
    label: 'Eastern Time (New York)',
    region: 'Americas',
    offsetHours: -5,
    offsetString: 'UTC-05:00',
  },
  {
    value: 'America/Chicago',
    label: 'Central Time (Chicago)',
    region: 'Americas',
    offsetHours: -6,
    offsetString: 'UTC-06:00',
  },
  {
    value: 'America/Denver',
    label: 'Mountain Time (Denver)',
    region: 'Americas',
    offsetHours: -7,
    offsetString: 'UTC-07:00',
  },
  {
    value: 'America/Los_Angeles',
    label: 'Pacific Time (Los Angeles)',
    region: 'Americas',
    offsetHours: -8,
    offsetString: 'UTC-08:00',
  },
  {
    value: 'America/Anchorage',
    label: 'Alaska Time (Anchorage)',
    region: 'Americas',
    offsetHours: -9,
    offsetString: 'UTC-09:00',
  },
  {
    value: 'Pacific/Honolulu',
    label: 'Hawaii Time (Honolulu)',
    region: 'Pacific',
    offsetHours: -10,
    offsetString: 'UTC-10:00',
  },
  {
    value: 'America/Toronto',
    label: 'Eastern Time (Toronto)',
    region: 'Americas',
    offsetHours: -5,
    offsetString: 'UTC-05:00',
  },
  {
    value: 'America/Vancouver',
    label: 'Pacific Time (Vancouver)',
    region: 'Americas',
    offsetHours: -8,
    offsetString: 'UTC-08:00',
  },
  {
    value: 'America/Mexico_City',
    label: 'Central Time (Mexico City)',
    region: 'Americas',
    offsetHours: -6,
    offsetString: 'UTC-06:00',
  },
  {
    value: 'America/Sao_Paulo',
    label: 'Brasília Time (São Paulo)',
    region: 'Americas',
    offsetHours: -3,
    offsetString: 'UTC-03:00',
  },
  {
    value: 'America/Argentina/Buenos_Aires',
    label: 'Argentina Time (Buenos Aires)',
    region: 'Americas',
    offsetHours: -3,
    offsetString: 'UTC-03:00',
  },

  // Europe
  {
    value: 'Europe/London',
    label: 'GMT/BST (London)',
    region: 'Europe',
    offsetHours: 0,
    offsetString: 'UTC+00:00',
  },
  {
    value: 'Europe/Paris',
    label: 'CET (Paris)',
    region: 'Europe',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Europe/Berlin',
    label: 'CET (Berlin)',
    region: 'Europe',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Europe/Amsterdam',
    label: 'CET (Amsterdam)',
    region: 'Europe',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Europe/Madrid',
    label: 'CET (Madrid)',
    region: 'Europe',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Europe/Rome',
    label: 'CET (Rome)',
    region: 'Europe',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Europe/Stockholm',
    label: 'CET (Stockholm)',
    region: 'Europe',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Europe/Zurich',
    label: 'CET (Zurich)',
    region: 'Europe',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Europe/Athens',
    label: 'EET (Athens)',
    region: 'Europe',
    offsetHours: 2,
    offsetString: 'UTC+02:00',
  },
  {
    value: 'Europe/Helsinki',
    label: 'EET (Helsinki)',
    region: 'Europe',
    offsetHours: 2,
    offsetString: 'UTC+02:00',
  },
  {
    value: 'Europe/Moscow',
    label: 'MSK (Moscow)',
    region: 'Europe',
    offsetHours: 3,
    offsetString: 'UTC+03:00',
  },

  // Asia
  {
    value: 'Asia/Dubai',
    label: 'Gulf Time (Dubai)',
    region: 'Asia',
    offsetHours: 4,
    offsetString: 'UTC+04:00',
  },
  {
    value: 'Asia/Kolkata',
    label: 'IST (Mumbai/Delhi)',
    region: 'Asia',
    offsetHours: 5.5,
    offsetString: 'UTC+05:30',
  },
  {
    value: 'Asia/Bangkok',
    label: 'ICT (Bangkok)',
    region: 'Asia',
    offsetHours: 7,
    offsetString: 'UTC+07:00',
  },
  {
    value: 'Asia/Singapore',
    label: 'SGT (Singapore)',
    region: 'Asia',
    offsetHours: 8,
    offsetString: 'UTC+08:00',
  },
  {
    value: 'Asia/Hong_Kong',
    label: 'HKT (Hong Kong)',
    region: 'Asia',
    offsetHours: 8,
    offsetString: 'UTC+08:00',
  },
  {
    value: 'Asia/Shanghai',
    label: 'CST (Shanghai)',
    region: 'Asia',
    offsetHours: 8,
    offsetString: 'UTC+08:00',
  },
  {
    value: 'Asia/Taipei',
    label: 'CST (Taipei)',
    region: 'Asia',
    offsetHours: 8,
    offsetString: 'UTC+08:00',
  },
  {
    value: 'Asia/Tokyo',
    label: 'JST (Tokyo)',
    region: 'Asia',
    offsetHours: 9,
    offsetString: 'UTC+09:00',
  },
  {
    value: 'Asia/Seoul',
    label: 'KST (Seoul)',
    region: 'Asia',
    offsetHours: 9,
    offsetString: 'UTC+09:00',
  },

  // Pacific / Oceania
  {
    value: 'Australia/Perth',
    label: 'AWST (Perth)',
    region: 'Pacific',
    offsetHours: 8,
    offsetString: 'UTC+08:00',
  },
  {
    value: 'Australia/Adelaide',
    label: 'ACST (Adelaide)',
    region: 'Pacific',
    offsetHours: 9.5,
    offsetString: 'UTC+09:30',
  },
  {
    value: 'Australia/Sydney',
    label: 'AEST (Sydney)',
    region: 'Pacific',
    offsetHours: 10,
    offsetString: 'UTC+10:00',
  },
  {
    value: 'Australia/Melbourne',
    label: 'AEST (Melbourne)',
    region: 'Pacific',
    offsetHours: 10,
    offsetString: 'UTC+10:00',
  },
  {
    value: 'Australia/Brisbane',
    label: 'AEST (Brisbane)',
    region: 'Pacific',
    offsetHours: 10,
    offsetString: 'UTC+10:00',
  },
  {
    value: 'Pacific/Auckland',
    label: 'NZST (Auckland)',
    region: 'Pacific',
    offsetHours: 12,
    offsetString: 'UTC+12:00',
  },

  // Africa
  {
    value: 'Africa/Cairo',
    label: 'EET (Cairo)',
    region: 'Africa',
    offsetHours: 2,
    offsetString: 'UTC+02:00',
  },
  {
    value: 'Africa/Johannesburg',
    label: 'SAST (Johannesburg)',
    region: 'Africa',
    offsetHours: 2,
    offsetString: 'UTC+02:00',
  },
  {
    value: 'Africa/Lagos',
    label: 'WAT (Lagos)',
    region: 'Africa',
    offsetHours: 1,
    offsetString: 'UTC+01:00',
  },
  {
    value: 'Africa/Nairobi',
    label: 'EAT (Nairobi)',
    region: 'Africa',
    offsetHours: 3,
    offsetString: 'UTC+03:00',
  },

  // Other
  {
    value: 'UTC',
    label: 'UTC',
    region: 'Other',
    offsetHours: 0,
    offsetString: 'UTC+00:00',
  },
];

/**
 * Get timezone options grouped by region.
 * Uses the curated list for better UX, with dynamic offset calculation.
 *
 * @param includeAllTimezones - If true, includes all IANA timezones. Default false (curated list only).
 */
export function getTimezoneOptions(includeAllTimezones = false): TimezoneOption[] {
  if (includeAllTimezones) {
    return getAllTimezones().map(buildTimezoneOption);
  }

  // Use curated list but update offsets dynamically (for DST)
  return CURATED_TIMEZONES.map((tz) => ({
    ...tz,
    offsetHours: getTimezoneOffset(tz.value),
    offsetString: formatTimezoneOffset(getTimezoneOffset(tz.value)),
  }));
}

/**
 * Get timezone options grouped by region.
 */
export function getTimezoneOptionsByRegion(
  includeAllTimezones = false
): Record<TimezoneRegion, TimezoneOption[]> {
  const options = getTimezoneOptions(includeAllTimezones);

  return options.reduce(
    (acc, tz) => {
      const region = tz.region as TimezoneRegion;
      if (!acc[region]) acc[region] = [];
      acc[region].push(tz);
      return acc;
    },
    {} as Record<TimezoneRegion, TimezoneOption[]>
  );
}

/**
 * Find the label for a timezone value.
 */
export function getTimezoneLabel(timezoneValue: string | null | undefined): string {
  if (!timezoneValue) return 'UTC';

  // Check curated list first
  const curated = CURATED_TIMEZONES.find((tz) => tz.value === timezoneValue);
  if (curated) return curated.label;

  // Fall back to building a label dynamically
  return buildTimezoneOption(timezoneValue).label;
}

/**
 * Detect the user's timezone from the browser/device.
 * Falls back to UTC if detection fails.
 */
export function detectUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}
